import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import { useLocation } from '../../contexts/LocationContext';
import { useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import EVChargingInfrastructure from '../charts/EVChargingInfrastructure';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { VMTChart, VMTMetrics } from '../charts/VMTChart';
import { VMTComparisonChart } from '../charts/VMTComparisonChart';
import { PageHeader } from '../layout/PageHeader';
import EVAdoptionBarriersChart from '../charts/EVAdoptionBarriersChart';
import CommutePatternsSection, {
  COMMUTE_PATTERN_OPTIONS,
  type CommutePatternView
} from '../charts/CommutePatternsSection';
import TransportationBurdenSection from '../charts/TransportationBurdenSection';
import WalkabilityTractSection from '../charts/WalkabilityTractSection';
import FleetCompositionSection from '../charts/FleetCompositionSection';
import { fetchWalkabilityPlaceMetrics } from '../../services/walkabilityTransportMetrics';
import { bindDownloadHandler } from '../../utils/downloadUtils';
import GenericMapTiered from '../charts/GenericMapTiered';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import SectionWrapper, { useSectionReveal } from './SectionWrapper';
import {
  buildVmtAuthoredBundle,
  buildVmtKeyInsights,
  buildVmtOverviewDescription,
  type VmtChartViewId,
} from '../../domains/narrative/vmtNarrative';
import {
  evChargingOverviewDescription,
  buildEvChargingAuthoredBundle,
  buildEvChargingKeyInsights,
  type EvChargingNarrativeSnapshot,
} from '../../domains/narrative/evChargingNarrative';
import { transportMapMethodology } from '../../domains/narrative/transportMapMethodology';
import {
  resolveMethodologyContentProps,
  type StructuredMethodology,
} from '../../domains/narrative/methodologyContentProps';
import { AuthoredEquityPanel, AuthoredRelevancePanel } from '../../domains/narrative/narrativeSectionContent';
import { buildVmtStructuredMethodologyForView } from '../../domains/narrative/vmtNarrative';
import { formatSignedPercentChange } from '../../domains/narrative/narrativeFormatting';
import {
  buildEvAdoptionAuthoredBundle,
  buildEvAdoptionKeyInsights,
  evAdoptionOverviewDescription,
  type EvAdoptionNarrativeInput,
  type EvAdoptionViewId,
} from '../../domains/narrative/electricVehicleAdoptionNarrative';
import {
  buildWalkabilityTransportAuthoredBundle,
  walkabilityTransportOverviewDescription,
} from '../../domains/narrative/walkabilityTransportNarrative';
import type { WalkabilityPlaceMetrics } from '../../services/walkabilityTransportMetrics';
import {
  buildTransportMapAuthoredBundle,
  buildTransportMapKeyInsights,
  transportMapOverviewDescription,
} from '../../domains/narrative/transportMapNarrative';
import {
  buildDrivingFuelCostAuthoredBundle,
  buildDrivingFuelCostKeyInsights,
  drivingFuelCostOverviewDescription,
  FUEL_COST_NARRATIVE_VIEW_ID,
} from '../../domains/narrative/estimatedDrivingFuelCostNarrative';

import vmtBg from '../../assets/transportation/vmt-bg.jpg';
import meansOfTransportationBg from '../../assets/transportation/means-of-transportation-bg.jpg';
import transportationBurdenBg from '../../assets/transportation/transportation-burden-bg.jpg';
import evAdoptionBg from '../../assets/transportation/ev-adoption-bg.jpg';
import evChargingBg from '../../assets/transportation/ev-charging-bg.jpg';

const TRANSPORT_SECTION_BG: Record<string, string> = {
  vmt: vmtBg,
  commute_patterns: meansOfTransportationBg,
  transportation_burden: transportationBurdenBg,
  walkability: meansOfTransportationBg,
  fleet_composition: vmtBg,
  ev_adoption: evAdoptionBg,
  ev_charging: evChargingBg,
};

const TRANSPORT_SECTION_IDS = Object.keys(TRANSPORT_SECTION_BG);

/** ~30% taller than default GenericMapTiered map (500px → 650px). */
const TRANSPORT_MAP_HEIGHT_PX = 650;

interface TransportationProps {
  location?: string;
}

interface TransportMapMetrics {
  currentValue: number;
  californiaAverage: number | null;
  year: number;
  variable: string;
  jurisdictionLabel?: string;
  referenceLabel?: string;
  minValue?: number;
  maxValue?: number;
  tractCount?: number;
  medianTractValue?: number;
  zeroPortTractCount?: number;
  evCountEstimated?: boolean;
}

interface TransportationBurdenNarrative {
  locationBurdenPct: number | null;
  californiaBurdenPct: number | null;
  locationMonthlyCost: number | null;
  californiaMonthlyCost: number | null;
  year: number | null;
}

type EvOverviewState = {
  observedYear: number;
  pluginCount: number;
  pluginRate: number;
  changeSince2020: number | null;
  pluginSharePct: number;
  projected2030?: number;
  target2030?: number;
};

function toTransportMapNarrativeInput(locationName: string, metrics: TransportMapMetrics) {
  return {
    locationName,
    layerId: metrics.variable,
    year: metrics.year,
    tractCount: metrics.tractCount ?? null,
    medianTractValue: metrics.medianTractValue ?? null,
    minTractValue: metrics.minValue ?? null,
    maxTractValue: metrics.maxValue ?? null,
    jurisdictionValue: metrics.currentValue,
    referenceValue: metrics.californiaAverage,
    referenceLabel: metrics.referenceLabel ?? 'California reference',
    evCountEstimated: metrics.evCountEstimated,
    zeroPortTractCount: metrics.zeroPortTractCount ?? null,
  };
}

function buildEvAdoptionNarrativeInput(
  locationName: string,
  viewId: EvAdoptionViewId,
  overview: EvOverviewState
): EvAdoptionNarrativeInput {
  return {
    locationName,
    viewId,
    observedYear: overview.observedYear,
    pluginCount: overview.pluginCount,
    pluginRate: overview.pluginRate,
    pluginSharePct: overview.pluginSharePct,
    changeSince2020: overview.changeSince2020,
    projected2030: overview.projected2030 ?? null,
    target2030: overview.target2030 ?? null,
  };
}

export const Transportation: React.FC<TransportationProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const [activeVMTView, setActiveVMTView] = useState<string>('overview');
  const [activeFuelCostView, setActiveFuelCostView] = useState<
    'overview' | 'relevance' | 'equity' | 'methodology'
  >('overview');
  const [activeEVView, setActiveEVView] = useState<string>('overview');
  const [activeChargingView, setActiveChargingView] = useState<string>('overview');
  const [burdenChartView, setBurdenChartView] = useState<'burden' | 'cost'>('cost');
  const [vmtChartView, setVmtChartView] = useState<string>('total');
  const [vmtMetrics, setVmtMetrics] = useState<VMTMetrics | null>(null);
  const [evChargingNarrative, setEvChargingNarrative] = useState<EvChargingNarrativeSnapshot | null>(null);
  // VMT per capita metrics removed - no longer needed
  const [evOverview, setEvOverview] = useState<EvOverviewState | null>(null);
  const [walkabilityMetrics, setWalkabilityMetrics] = useState<WalkabilityPlaceMetrics | null>(null);
  const [evAdoptionMapMetrics, setEvAdoptionMapMetrics] = useState<TransportMapMetrics | null>(null);
  const [evChargingView, setEvChargingView] = useState<'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map'>('peoplePerCharger');
  const [evAdoptionView, setEvAdoptionView] = useState<'perCapita' | 'projection' | 'map'>('perCapita');
  const [vmtDataTableContent, setVmtDataTableContent] = useState<React.ReactNode>(null);
  const [vmtShowDataTable, setVmtShowDataTable] = useState(false);
  const [vmtDownloadPNG, setVmtDownloadPNG] = useState<(() => void) | null>(null);
  const [vmtDownloadCSV, setVmtDownloadCSV] = useState<(() => void) | null>(null);
  const [comparisonDownloadPNG, setComparisonDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacCountyDownloadPNG, setEmfacCountyDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacFleetDownloadPNG, setEmfacFleetDownloadPNG] = useState<(() => void) | null>(null);
  const [transportMapMetrics, setTransportMapMetrics] = useState<TransportMapMetrics | null>(null);
  const [activeTransportMapView, setActiveTransportMapView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [commutePatternView, setCommutePatternView] = useState<CommutePatternView>('modes');
  // State for VMT backcasting options
  const [vmt2005_2014Method, setVmt2005_2014Method] = useState<'EMFAC2025' | 'Population' | 'Combined'>('Combined');
  const [vmtBackcastingMethod, setVmtBackcastingMethod] = useState<
    'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Combined'
  >('Combined');
  const [burdenNarrative, setBurdenNarrative] = useState<TransportationBurdenNarrative | null>(null);
  const [activeWalkabilityView, setActiveWalkabilityView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [showWalkabilitySection, setShowWalkabilitySection] = useState(false);

  const vmtDownloadPNGRef = useRef<(() => void) | null>(null);
  const comparisonDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacCountyDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacFleetDownloadPNGRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    vmtDownloadPNGRef.current = vmtDownloadPNG;
  }, [vmtDownloadPNG]);
  useEffect(() => {
    comparisonDownloadPNGRef.current = comparisonDownloadPNG;
  }, [comparisonDownloadPNG]);
  useEffect(() => {
    emfacCountyDownloadPNGRef.current = emfacCountyDownloadPNG;
  }, [emfacCountyDownloadPNG]);
  useEffect(() => {
    emfacFleetDownloadPNGRef.current = emfacFleetDownloadPNG;
  }, [emfacFleetDownloadPNG]);

  // Scroll to chart section when opening with a hash (e.g. livability → Transportation Cost Burden).
  useEffect(() => {
    const applyCommuteHash = (hash: string) => {
      if (hash === 'departure_time') setCommutePatternView('departure');
      else if (hash === 'commute_time_extreme' || hash === 'commute_time') setCommutePatternView('times');
      else if (hash === 'means_of_transportation' || hash === 'mode_share' || hash === 'commute_patterns') {
        setCommutePatternView('modes');
      }
    };
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      applyCommuteHash(hash);
      const el = document.getElementById(hash) || (hash === 'departure_time' || hash === 'means_of_transportation' || hash === 'commute_time_extreme' || hash === 'mode_share' || hash === 'commute_time'
        ? document.getElementById('commute_patterns')
        : null);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    applyCommuteHash(hash);
    const t1 = setTimeout(scrollToHash, 150);
    const t2 = setTimeout(scrollToHash, 500);
    const t3 = setTimeout(scrollToHash, 900);
    const onHashChange = () => {
      setTimeout(scrollToHash, 80);
      setTimeout(scrollToHash, 400);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  const currentLocation = propLocation || selectedLocation;
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  // Register every figure (each chart view) so the download modal lists all of them.
  // Deferred so registration runs after first paint and does not block load; stable deps to avoid re-run loops.
  useEffect(() => {
    if (!currentLocation) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      const loc = currentLocation.replace(/\s+/g, '_');
      const delay = 600;
      const unreg: string[] = [];

      // VMT (5 views) - each switches view then runs that view's export after delay (pageId so these only show on Transportation tab)
      registerPng('VMT_Total', 'Vehicle Miles Traveled (Total VMT)', () => {
      setVmtChartView('total');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_Total');
    registerPng('VMT_PerCapita', 'Vehicle Miles Traveled (Per Capita)', () => {
      setVmtChartView('percapita');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_PerCapita');
    registerPng('VMT_Comparison', 'VMT Comparison (Light-Duty vs Heavy-Duty)', () => {
      setVmtChartView('comparison');
      setTimeout(() => comparisonDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_Comparison');
    // EV Adoption (3 views) - switch view then export canvas
    const evAdoptionViews = [
      { id: 'EV_Adoption_Projection', label: 'Electric Vehicle Adoption (Projection to 2030)', view: 'projection' as const },
      { id: 'EV_Adoption_PerCapita', label: 'Electric Vehicle Adoption (Per 1,000 residents)', view: 'perCapita' as const },
      { id: 'EV_Adoption_Map', label: 'Electric Vehicle Adoption (Tract Map)', view: 'map' as const }
    ];
    evAdoptionViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvAdoptionView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `EV_Adoption_${view === 'projection' ? 'Projection' : view === 'perCapita' ? 'Per_Capita' : 'Tract_Map'}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      }, 'transportation');
      unreg.push(id);
    });

    // EV Charging (4 views)
    const evChargingViews = [
      { id: 'EV_Charging_People', label: 'EV Charging (People per Charger)', view: 'peoplePerCharger' as const },
      { id: 'EV_Charging_Counts', label: 'EV Charging (Total Chargers)', view: 'counts' as const },
      { id: 'EV_Charging_EVs', label: 'EV Charging (EVs per Charger)', view: 'evsPerCharger' as const },
      { id: 'EV_Charging_Map', label: 'EV Charging (Tract Map)', view: 'map' as const }
    ];
    evChargingViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvChargingView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = view === 'counts' ? 'Total_Chargers' : view === 'peoplePerCharger' ? 'People_Per_Charger' : view === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map';
            link.download = `EV_Charging_${viewLabel}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      }, 'transportation');
      unreg.push(id);
    });

    // Transportation-Related Maps (1 - current map variable)
    registerPng('Transportation_Maps', 'Transportation-Related Maps', () => {
      const container = document.querySelector('#transportation-maps-chart');
      const canvas = container?.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `Transportation_Maps_${loc}.png`;
        link.href = (canvas as HTMLCanvasElement).toDataURL('image/png');
        link.click();
      }
    }, 'transportation');
        unreg.push('Transportation_Maps');

    const commuteViews = [
      { id: 'Commute_Patterns_Modes', label: 'Commute Patterns (Transportation Modes)', view: 'modes' as const, file: 'Transportation_Modes' },
      { id: 'Commute_Patterns_Times', label: 'Commute Patterns (Commute Times)', view: 'times' as const, file: 'Commute_Times' },
      { id: 'Commute_Patterns_Departure', label: 'Commute Patterns (Departure Time)', view: 'departure' as const, file: 'Departure_Time' }
    ];
    commuteViews.forEach(({ id, label, view, file }) => {
      registerPng(id, label, () => {
        setCommutePatternView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#commute-patterns-chart [data-active] canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Commute_Patterns_${file}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      }, 'transportation');
      unreg.push(id);
    });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
      // Unregister all in case we unmount before timeout runs
      ['VMT_Total', 'VMT_PerCapita', 'VMT_Comparison',
       'EV_Adoption_Projection', 'EV_Adoption_PerCapita', 'EV_Adoption_Map',
       'EV_Charging_People', 'EV_Charging_Counts', 'EV_Charging_EVs', 'EV_Charging_Map',
       'Transportation_Maps',
       'Commute_Patterns_Modes', 'Commute_Patterns_Times', 'Commute_Patterns_Departure'].forEach((id) => unregisterPng(id));
    };
  }, [currentLocation, registerPng, unregisterPng]);
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('transportation', currentLocation);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = currentLocation?.trim();
      if (!loc) {
        setShowWalkabilitySection(false);
        return;
      }
      try {
        const m = await fetchWalkabilityPlaceMetrics(loc);
        if (!cancelled) {
          setWalkabilityMetrics(m);
          setShowWalkabilitySection(m != null && m.tractCount > 0);
        }
      } catch {
        if (!cancelled) setShowWalkabilitySection(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentLocation]);

  const vmtOptions = [
    { id: 'vehicle_miles', value: 'total', label: 'Light-duty VMT' },
    { id: 'vmt_percapita', value: 'percapita', label: 'Per Capita' },
    { id: 'vmt_comparison', value: 'comparison', label: 'VMT Comparison' }
  ];
  const allowedVmtOptions = vmtOptions
    .filter((option) => isChartEnabled('vmt', option.id))
    .sort((a, b) => getChartOrder('vmt', a.id) - getChartOrder('vmt', b.id));

  const evAdoptionOptions = [
    { id: 'ev_fleet_adoption', value: 'perCapita', label: 'Per 1,000 Residents' },
    { id: 'ev_fleet_adoption', value: 'projection', label: 'Scenario to 2030 (illustrative)' },
    { id: 'ev_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvAdoptionOptions = evAdoptionOptions
    .filter((option) => isChartEnabled('ev_adoption', option.id))
    .sort((a, b) => getChartOrder('ev_adoption', a.id) - getChartOrder('ev_adoption', b.id));

  const evChargingOptions = [
    { id: 'ev_charging_people', value: 'peoplePerCharger', label: 'People per Charger' },
    { id: 'ev_charging_counts', value: 'counts', label: 'Total Chargers' },
    { id: 'ev_charging_evs', value: 'evsPerCharger', label: 'EVs per Charger' },
    { id: 'ev_charging_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvChargingOptions = evChargingOptions
    .filter((option) => isChartEnabled('ev_charging', option.id))
    .sort((a, b) => getChartOrder('ev_charging', a.id) - getChartOrder('ev_charging', b.id));

  const allowedCommutePatternOptions = COMMUTE_PATTERN_OPTIONS
    .filter((option) => isSectionEnabled(option.sectionId) && isChartEnabled(option.sectionId, option.chartId))
    .map((option) => ({ value: option.value, label: option.label }));
  const commutePatternsEnabled = allowedCommutePatternOptions.length > 0;
  const commutePatternsOrder = commutePatternsEnabled
    ? Math.min(
        ...COMMUTE_PATTERN_OPTIONS
          .filter((option) => isSectionEnabled(option.sectionId) && isChartEnabled(option.sectionId, option.chartId))
          .map((option) => getSectionOrder(option.sectionId))
      )
    : 999;

  const transportMapVariables = [
    { id: 'ev_adoption', value: 'ev_adoption' },
    { id: 'total_evs', value: 'total_evs' },
    { id: 'total_chargers', value: 'total_chargers' },
    { id: 'vehicles_per_household', value: 'vehicles_per_household' },
    { id: 'zero_vehicle_households', value: 'zero_vehicle_households' },
    { id: 'commute_time', value: 'commute_time' }
  ];
  const allowedTransportMapVariables = transportMapVariables
    .filter((option) => isChartEnabled('transportation_maps', option.id))
    .sort((a, b) => getChartOrder('transportation_maps', a.id) - getChartOrder('transportation_maps', b.id))
    .map((option) => option.value);
  const transportMapsEnabled =
    isSectionEnabled('transportation_maps') && allowedTransportMapVariables.length > 0;


  React.useEffect(() => {
    if (allowedVmtOptions.length && !allowedVmtOptions.some((opt) => opt.value === vmtChartView)) {
      setVmtChartView(allowedVmtOptions[0].value);
    }
  }, [allowedVmtOptions, vmtChartView]);

  React.useEffect(() => {
    if (allowedEvAdoptionOptions.length && !allowedEvAdoptionOptions.some((opt) => opt.value === evAdoptionView)) {
      setEvAdoptionView(allowedEvAdoptionOptions[0].value as 'perCapita' | 'projection' | 'map');
    }
  }, [allowedEvAdoptionOptions, evAdoptionView]);

  React.useEffect(() => {
    if (allowedEvChargingOptions.length && !allowedEvChargingOptions.some((opt) => opt.value === evChargingView)) {
      setEvChargingView(allowedEvChargingOptions[0].value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map');
    }
  }, [allowedEvChargingOptions, evChargingView]);

  React.useEffect(() => {
    if (
      allowedCommutePatternOptions.length &&
      !allowedCommutePatternOptions.some((opt) => opt.value === commutePatternView)
    ) {
      setCommutePatternView(allowedCommutePatternOptions[0].value);
    }
  }, [allowedCommutePatternOptions, commutePatternView]);

  const getSectionStyle = (sectionId: string) => {
    if (sectionId === 'commute_patterns') {
      return {
        order: commutePatternsOrder,
        display: commutePatternsEnabled ? 'block' : 'none'
      };
    }
    return {
      order: getSectionOrder(sectionId),
      display: isSectionEnabled(sectionId) ? 'block' : 'none'
    };
  };

  const { visible: sectionVisible, refs: sectionRefs } = useSectionReveal(
    TRANSPORT_SECTION_IDS,
    !!currentLocation
  );

  const toggleVMTView = (view: string) => {
    setActiveVMTView(view);
  };

  const toggleEVView = (view: string) => {
    setActiveEVView(view);
  };




  const handleVMTMetricsCalculated = useCallback((metrics: VMTMetrics) => {
    setVmtMetrics(metrics);
  }, []);

  // VMT per capita metrics handler removed - no longer needed

  const handleVMTDataCalculated = useCallback((data: any[]) => {
    if (!currentLocation) return;

    setVMTDataFromTransportation(data, currentLocation);

    if (typeof window !== 'undefined') {
      window.vmtChartData = data;
    }
  }, [currentLocation]);


  const handleTransportMapDataLoaded = useCallback((metrics: TransportMapMetrics) => {
    setTransportMapMetrics(metrics);
  }, []);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Transportation & Mobility" 
          subtitle="Please select a location to view transportation data."
        />
      </div>
    );
  }

  return (
    <div data-report-root="transportation" data-outreach-snapshot="transportation">
      <PageHeader 
        title={`Transportation & Mobility - ${currentLocation}`}
        subtitle={`Travel activity, vehicle technology, access, affordability, and transportation options in ${currentLocation}.`}
        variant="transportation"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <SectionWrapper sectionId="vmt" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      {/* Vehicle Miles Traveled Section */}
      <ChartLayout
        className="vmt-compact"
        title="Vehicle Miles Traveled (VMT)"
        narrativeViewId={vmtChartView}
        chartType="bar"
        keyMetrics={
          <KeyMetrics className="key-metrics-four">
            {vmtChartView === 'total' ? (
              <>
                <KeyMetric
                  value={vmtMetrics ? vmtMetrics.reportingYearLdvVmt.toLocaleString() : '…'}
                  label={vmtMetrics ? `Annual light-duty VMT (${vmtMetrics.reportingYear})` : 'Annual light-duty VMT'}
                />
                <KeyMetric
                  value={
                    vmtMetrics?.changeSince2019Annual == null
                      ? '—'
                      : formatSignedPercentChange(vmtMetrics.changeSince2019Annual)
                  }
                  label="Change since 2019 (annual LDV)"
                />
                <KeyMetric
                  value={vmtMetrics ? String(vmtMetrics.reportingYear) : '—'}
                  label="Reporting year (local annual LDV series)"
                />
                <KeyMetric
                  value={
                    vmtMetrics?.pctVMTFromLightDuty != null
                      ? `${vmtMetrics.pctVMTFromLightDuty}%`
                      : '—'
                  }
                  label="LDV share of LDV+HDV (reporting year)"
                />
              </>
            ) : vmtChartView === 'comparison' ? (
              <KeyMetric value="LDV + HDV" label="Separate series — see chart legend" />
            ) : (
              <>
                <KeyMetric
                  value={vmtMetrics ? `${vmtMetrics.currentVMT}` : '…'}
                  label={vmtMetrics ? `Daily LDV VMT per resident (${vmtMetrics.reportingYear})` : 'Daily LDV VMT per resident'}
                  color={
                    vmtMetrics?.californiaAverageReportingYear != null &&
                    vmtMetrics.currentVMT < vmtMetrics.californiaAverageReportingYear
                      ? '#10b981'
                      : '#ef4444'
                  }
                />
                <KeyMetric
                  value={
                    vmtMetrics?.changeSince2019 == null
                      ? '—'
                      : formatSignedPercentChange(vmtMetrics.changeSince2019)
                  }
                  label="Change since 2019 (per capita)"
                />
                <KeyMetric
                  value={
                    vmtMetrics?.californiaAverageReportingYear != null
                      ? `${vmtMetrics.californiaAverageReportingYear}`
                      : '—'
                  }
                  label={vmtMetrics ? `California daily LDV VMT per resident (${vmtMetrics.reportingYear})` : 'California comparison'}
                  color="#f59e0b"
                />
                <KeyMetric
                  value={
                    vmtMetrics?.californiaAverageReportingYear != null
                      ? `${(vmtMetrics.currentVMT - vmtMetrics.californiaAverageReportingYear).toFixed(1)}`
                      : '—'
                  }
                  label="Difference from California (mi/day)"
                  color="#1e3a8a"
                />
              </>
            )}
          </KeyMetrics>
        }
        chartContent={
          vmtChartView === 'comparison' ? (
            <VMTComparisonChart
              location={currentLocation}
              geofips={selectedGeofips}
              onDownloadPNGReady={bindDownloadHandler(setComparisonDownloadPNG)}
            />
          ) : (
            <VMTChart 
              location={currentLocation} 
              geofips={selectedGeofips || null} 
              chartView={vmtChartView as 'total' | 'percapita'} 
              emfac2005_2014Method={vmt2005_2014Method}
              backcastingMethod={vmtBackcastingMethod}
              emfacFleetVersion={vmtBackcastingMethod === 'EMFAC2021 Fleet' ? '2021' : '2025'}
              onMetricsCalculated={handleVMTMetricsCalculated} 
              onDataCalculated={handleVMTDataCalculated}
              onDataTableReady={(content, toggleFn) => {
                setVmtDataTableContent(content);
                // Store toggle function in a ref or state if needed
              }}
              onDownloadPNGReady={bindDownloadHandler(setVmtDownloadPNG)}
              onDownloadCSVReady={bindDownloadHandler(setVmtDownloadCSV)}
            />
          )
        }
        dataTableContent={vmtChartView !== 'comparison' ? vmtDataTableContent : undefined}
        showDataTable={vmtChartView !== 'comparison' ? vmtShowDataTable : false}
        onToggleDataTable={() => setVmtShowDataTable(!vmtShowDataTable)}
        onDownloadPNG={
          vmtChartView === 'comparison' ? comparisonDownloadPNG ?? undefined : vmtDownloadPNG ?? undefined
        }
        onDownloadCSV={vmtChartView !== 'comparison' && vmtDownloadCSV ? vmtDownloadCSV : undefined}
        pngFilename={`VMT_Chart_${vmtChartView === 'total' ? 'Light_Duty' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`VMT_Data_${vmtChartView === 'total' ? 'Light_Duty' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        chartSelector={{
          options: allowedVmtOptions.map((option) => ({ value: option.value, label: option.label })),
          value: vmtChartView,
          onChange: setVmtChartView
        }}
        additionalButtonGroups={vmtChartView !== 'comparison' ? [
          {
            label: '(2005 - 2014)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'Population', label: 'Population' }
            ],
            value: vmt2005_2014Method,
            onChange: (value) => setVmt2005_2014Method(value as 'EMFAC2025' | 'Population' | 'Combined')
          },
          {
            label: '(2015-2022)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'AADT', label: 'AADT' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'EMFAC Fleet', label: 'EMFAC2025 Fleet' },
              { value: 'EMFAC2021 Fleet', label: 'EMFAC2021 Fleet' }
            ],
            value: vmtBackcastingMethod,
            onChange: (value) => setVmtBackcastingMethod(value as 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Combined')
          }
        ] : undefined}
        overviewContent={
          (() => {
            const input =
              vmtMetrics
                ? {
                    locationName: currentLocation,
                    viewId: vmtChartView as VmtChartViewId,
                    reportingYear: vmtMetrics.reportingYear,
                    perCapitaMiPerDay: vmtMetrics.currentVMT,
                    changeSince2019Pct: vmtMetrics.changeSince2019,
                    californiaPerCapitaMiPerDay: vmtMetrics.californiaAverageReportingYear,
                    annualLdvVmt: vmtMetrics.reportingYearLdvVmt,
                    changeSince2019AnnualPct: vmtMetrics.changeSince2019Annual,
                    californiaAnnualLdvVmt: vmtMetrics.californiaAnnualLdvVmtReportingYear,
                    annualLdvSeries: vmtMetrics.annualLdvSeries,
                  }
                : null;
            const authored = input ? buildVmtAuthoredBundle(input) : null;
            return (
              <OverviewContent
                title="Overview"
                description={
                  authored?.overview || buildVmtOverviewDescription(vmtChartView as VmtChartViewId)
                }
              >
                <p>
                  {input ? authored?.keyInsights || buildVmtKeyInsights(input) : `Loading VMT data for ${currentLocation}...`}
                </p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const input = vmtMetrics
              ? {
                  locationName: currentLocation,
                  viewId: vmtChartView as VmtChartViewId,
                  reportingYear: vmtMetrics.reportingYear,
                  perCapitaMiPerDay: vmtMetrics.currentVMT,
                  changeSince2019Pct: vmtMetrics.changeSince2019,
                  californiaPerCapitaMiPerDay: vmtMetrics.californiaAverageReportingYear,
                  annualLdvVmt: vmtMetrics.reportingYearLdvVmt,
                  changeSince2019AnnualPct: vmtMetrics.changeSince2019Annual,
                  californiaAnnualLdvVmt: vmtMetrics.californiaAnnualLdvVmtReportingYear,
                  annualLdvSeries: vmtMetrics.annualLdvSeries,
                }
              : null;
            const authored = input ? buildVmtAuthoredBundle(input) : null;
            if (authored) return <AuthoredRelevancePanel bundle={authored} />;
            return (
              <AuthoredRelevancePanel
                chartId="vmt"
                viewId={vmtChartView as string}
                body="VMT is the activity component of on-road emissions: total emissions depend on both miles traveled and emissions per mile."
              />
            );
          })()
        }
        equityContent={
          (() => {
            const input = vmtMetrics
              ? {
                  locationName: currentLocation,
                  viewId: vmtChartView as VmtChartViewId,
                  reportingYear: vmtMetrics.reportingYear,
                  perCapitaMiPerDay: vmtMetrics.currentVMT,
                  changeSince2019Pct: vmtMetrics.changeSince2019,
                  californiaPerCapitaMiPerDay: vmtMetrics.californiaAverageReportingYear,
                  annualLdvVmt: vmtMetrics.reportingYearLdvVmt,
                  changeSince2019AnnualPct: vmtMetrics.changeSince2019Annual,
                  californiaAnnualLdvVmt: vmtMetrics.californiaAnnualLdvVmtReportingYear,
                  annualLdvSeries: vmtMetrics.annualLdvSeries,
                }
              : null;
            const authored = input ? buildVmtAuthoredBundle(input) : null;
            if (authored?.equityV6 || authored?.equity) {
              return <AuthoredEquityPanel bundle={authored} />;
            }
            return (
              <AuthoredEquityPanel
                chartId="vmt"
                viewId={vmtChartView as string}
                body="Travel needs, vehicle access, and exposure to roadway pollution are unevenly distributed."
              />
            );
          })()
        }
        methodologyContent={
          (() => {
            const viewId = vmtChartView as VmtChartViewId;
            const structured = buildVmtStructuredMethodologyForView(viewId);
            const legacy =
              viewId === 'comparison'
                ? {
                    title: 'Methodology',
                    description:
                      'Light-duty and heavy-duty vehicle miles traveled are shown separately using compatible years only.',
                    dataSources: [],
                    methodology: [],
                  }
                : {
                    title: 'VMT methodology',
                    description:
                      'Light-duty VMT per resident combines Replica estimates for recent years with backcast series for 2005–2022. Values are not direct citywide traffic counts.',
                    dataSources: [],
                    methodology: [],
                  };
            const props = resolveMethodologyContentProps(structured, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        activeView={activeVMTView}
        onViewChange={toggleVMTView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="transportation"
        overviewChartId="vehicle_miles"
        registerPng={false}
      />
      </SectionWrapper>

      {/* Vehicle Fleet Composition (CARB EMFAC Fleet DB) — second section below VMT */}
      <SectionWrapper sectionId="fleet_composition" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <FleetCompositionSection location={currentLocation} geofips={selectedGeofips} />
      </SectionWrapper>

      {/* Electric Vehicle Adoption Section */}
      <SectionWrapper sectionId="ev_adoption" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="Electric Vehicle Adoption"
        chartType="line"
        chartSelector={{
          options: allowedEvAdoptionOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evAdoptionView,
          onChange: (value: string) => setEvAdoptionView(value as 'perCapita' | 'projection' | 'map')
        }}
        keyMetrics={
          evAdoptionView === 'map' && evAdoptionMapMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${evAdoptionMapMetrics.currentValue.toFixed(1)}`}
                label={evAdoptionMapMetrics.jurisdictionLabel ?? 'Plug-in LDVs per 100 residents'}
              />
              <KeyMetric
                value={
                  evAdoptionMapMetrics.californiaAverage != null
                    ? `${evAdoptionMapMetrics.californiaAverage.toFixed(1)}`
                    : '—'
                }
                label={evAdoptionMapMetrics.referenceLabel ?? 'California reference'}
              />
            </KeyMetrics>
          ) : evAdoptionView === 'projection' && evOverview ? (
            <KeyMetrics>
              <KeyMetric
                value={evOverview.pluginCount.toLocaleString()}
                label={`Observed plug-in LDVs (${evOverview.observedYear})`}
              />
              <KeyMetric
                value={
                  evOverview.projected2030 != null
                    ? Math.round(evOverview.projected2030).toLocaleString()
                    : '—'
                }
                label="Illustrative 2030 plug-in LDVs"
              />
              <KeyMetric
                value={
                  evOverview.projected2030 != null
                    ? `${Math.round(evOverview.projected2030 - evOverview.pluginCount).toLocaleString()}`
                    : '—'
                }
                label={`Change to illustrative 2030`}
              />
              <KeyMetric
                value={
                  evOverview.target2030 != null && evOverview.target2030 > 0
                    ? Math.round(evOverview.target2030).toLocaleString()
                    : '—'
                }
                label="Illustrative CA population-scaled reference"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric
                value={evOverview ? `${evOverview.pluginRate}` : 'Loading...'}
                label={
                  evOverview
                    ? `Plug-in LDVs per 1,000 residents (${evOverview.observedYear})`
                    : 'Plug-in LDVs per 1,000 residents'
                }
                color="#2563eb"
              />
              <KeyMetric
                value={evOverview ? evOverview.pluginCount.toLocaleString() : 'Loading...'}
                label={evOverview ? `Registered plug-in LDVs (${evOverview.observedYear})` : 'Registered plug-in LDVs'}
                color="#1e3a8a"
              />
              <KeyMetric
                value={evOverview ? `${evOverview.pluginSharePct}%` : 'Loading...'}
                label="Plug-in share of LDV fleet"
                color="#10b981"
              />
              <KeyMetric value="—" label="California rate (when same-year available)" />
            </KeyMetrics>
          )
        }
        chartContent={
          evAdoptionView === 'map' ? (
            <GenericMapTiered
              location={currentLocation}
              locationGeofips={selectedGeofips}
              mapHeightPx={TRANSPORT_MAP_HEIGHT_PX}
              hideVariableSelector={true}
              lockedVariable="ev_adoption"
              onDataLoaded={(metrics) => {
                if (metrics?.variable === 'ev_adoption') {
                  setEvAdoptionMapMetrics(metrics);
                }
              }}
            />
          ) : (
            <div id="ev-adoption-chart">
              <EVFleetAdoption 
                location={currentLocation} 
                geofips={selectedGeofips} 
                activeTab={evAdoptionView}
                onTabChange={setEvAdoptionView}
                onMetricsCalculated={setEvOverview} 
              />
            </div>
          )
        }
        narrativeViewId={evAdoptionView}
        overviewContent={
          (() => {
            if (evAdoptionView === 'map' && evAdoptionMapMetrics) {
              const authored = buildTransportMapAuthoredBundle(
                toTransportMapNarrativeInput(currentLocation, evAdoptionMapMetrics)
              );
              return (
                <OverviewContent title="Overview" description={authored.overview || ''}>
                  <p>{authored.keyInsights || buildTransportMapKeyInsights(toTransportMapNarrativeInput(currentLocation, evAdoptionMapMetrics))}</p>
                </OverviewContent>
              );
            }
            const input = evOverview ? buildEvAdoptionNarrativeInput(currentLocation, evAdoptionView, evOverview) : null;
            const authored = input ? buildEvAdoptionAuthoredBundle(input) : null;
            return (
              <OverviewContent
                title="Overview"
                description={authored?.overview || evAdoptionOverviewDescription(evAdoptionView)}
              >
                <p>
                  {input
                    ? authored?.keyInsights || buildEvAdoptionKeyInsights(input)
                    : `Loading EV adoption data for ${currentLocation}...`}
                </p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            if (evAdoptionView === 'map' && evAdoptionMapMetrics) {
              const bundle = buildTransportMapAuthoredBundle(
                toTransportMapNarrativeInput(currentLocation, evAdoptionMapMetrics)
              );
              return <AuthoredRelevancePanel bundle={bundle} />;
            }
            const input = evOverview ? buildEvAdoptionNarrativeInput(currentLocation, evAdoptionView, evOverview) : null;
            const authored = input ? buildEvAdoptionAuthoredBundle(input) : null;
            if (authored) return <AuthoredRelevancePanel bundle={authored} />;
            return (
              <AuthoredRelevancePanel
                chartId="electric_vehicle_adoption"
                viewId={evAdoptionView}
                body="Registered plug-in vehicle trends inform charging and grid planning."
              />
            );
          })()
        }
        equityContent={
          (() => {
            if (evAdoptionView === 'map' && evAdoptionMapMetrics) {
              const bundle = buildTransportMapAuthoredBundle(
                toTransportMapNarrativeInput(currentLocation, evAdoptionMapMetrics)
              );
              return <AuthoredEquityPanel bundle={bundle} />;
            }
            const input = evOverview ? buildEvAdoptionNarrativeInput(currentLocation, evAdoptionView, evOverview) : null;
            const authored = input ? buildEvAdoptionAuthoredBundle(input) : null;
            if (authored?.equityV6 || authored?.equity) {
              return <AuthoredEquityPanel bundle={authored} />;
            }
            return (
              <AuthoredEquityPanel
                chartId="electric_vehicle_adoption"
                viewId={evAdoptionView}
                body="Registration rates do not show who can afford an EV or access home charging."
              />
            );
          })()
        }
        methodologyContent={
          (() => {
            if (evAdoptionView === 'map' && evAdoptionMapMetrics) {
              const authoredM = buildTransportMapAuthoredBundle(
                toTransportMapNarrativeInput(currentLocation, evAdoptionMapMetrics)
              ).methodology;
              const legacy = transportMapMethodology('ev_adoption');
              const props = resolveMethodologyContentProps(authoredM, {
                title: 'Methodology',
                description: `${legacy.metricLabel} for the selected map layer.`,
                dataSources: legacy.primarySources,
                methodology: legacy.calculation,
              });
              return <MethodologyContent {...props} />;
            }
            const input = evOverview ? buildEvAdoptionNarrativeInput(currentLocation, evAdoptionView, evOverview) : null;
            const authored = input ? buildEvAdoptionAuthoredBundle(input) : null;
            const legacy = {
              title: 'Methodology',
              description:
                'Plug-in light-duty vehicle counts and rates from the California Air Resources Board EMFAC Fleet Database (registration-derived/model fleet population).',
              dataSources: [
                'California Air Resources Board — EMFAC Fleet Database (DMV registration-derived fleet population; verify EMFAC vintage per reporting year)',
                'U.S. Census Bureau population — same-year residents for per-1,000 rates',
              ],
              methodology: [
                'Filter class = LDV; plug-in count = Electric + PluginHybrid vehicle_population summed by year.',
                'Per 1,000 residents = plug-in count ÷ same-year population × 1,000.',
                'Fleet share = plug-in LDV count ÷ all LDV fuel types in the same year and geography.',
              ],
              technicalDetails: [
                'emfac2025_fleet_mat',
                'emfac2021_fleet_mat (fallback when 2025 rows missing)',
                'EVFleetAdoption.tsx',
              ],
            };
            const authoredMethodology =
              authored && 'methodology' in authored
                ? (authored as { methodology?: StructuredMethodology }).methodology
                : undefined;
            const props = resolveMethodologyContentProps(authoredMethodology, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
        activeView={activeEVView}
        onViewChange={toggleEVView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        overviewSectionId="transportation"
        overviewChartId="ev_fleet_adoption"
        onDownloadPNG={() => {
          if (evAdoptionView === 'map') {
            return;
          }
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection';
            link.download = `EV_Adoption_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // TODO: implement CSV download based on chart data
        }}
        pngFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
      />

      {/* EV Adoption Barriers by Income (within Electric Vehicle Adoption section) */}
      <EVAdoptionBarriersChart location={currentLocation} />
      </SectionWrapper>


      {/* Commute Patterns: modes, times, departure */}
      <SectionWrapper sectionId="commute_patterns" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <CommutePatternsSection
          location={currentLocation}
          geofips={selectedGeofips}
          chartView={commutePatternView}
          onChartViewChange={setCommutePatternView}
          allowedOptions={allowedCommutePatternOptions}
        />
      </SectionWrapper>

      {/* Transportation Burden — anchor for Climate Equity deep links */}
      <SectionWrapper sectionId="transportation_burden" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <div id="chart-transportation-burden">
        <ChartLayout
          title="Estimated Driving Fuel Cost"
          chartType="bar"
          narrativeViewId={FUEL_COST_NARRATIVE_VIEW_ID}
          overviewChartId="estimated_driving_fuel_cost"
          chartContent={
            <TransportationBurdenSection
              location={currentLocation}
              chartView="cost"
              onSummaryCalculated={setBurdenNarrative}
            />
          }
          overviewContent={
            (() => {
              const fuelInput = {
                locationName: currentLocation,
                year: burdenNarrative?.year ?? null,
                monthlyCostPerCapita: burdenNarrative?.locationMonthlyCost ?? null,
              };
              const authored = buildDrivingFuelCostAuthoredBundle(fuelInput);
              return (
                <OverviewContent
                  title="Overview"
                  description={authored.overview || drivingFuelCostOverviewDescription()}
                >
                  <p>
                    {authored.keyInsights ||
                      buildDrivingFuelCostKeyInsights(fuelInput) ||
                      `Loading driving fuel cost estimates for ${currentLocation}...`}
                  </p>
                </OverviewContent>
              );
            })()
          }
          relevanceContent={
            <AuthoredRelevancePanel
              body={
                buildDrivingFuelCostAuthoredBundle({
                  locationName: currentLocation,
                  year: burdenNarrative?.year ?? null,
                  monthlyCostPerCapita: burdenNarrative?.locationMonthlyCost ?? null,
                }).relevance ||
                'Fuel-cost estimates can provide context for the economic implications of driving.'
              }
            />
          }
          equityContent={
            <AuthoredEquityPanel
              bundle={buildDrivingFuelCostAuthoredBundle({
                locationName: currentLocation,
                year: burdenNarrative?.year ?? null,
                monthlyCostPerCapita: burdenNarrative?.locationMonthlyCost ?? null,
              })}
            />
          }
          methodologyContent={
            (() => {
              const authoredM = buildDrivingFuelCostAuthoredBundle({
                locationName: currentLocation,
                year: burdenNarrative?.year ?? null,
                monthlyCostPerCapita: burdenNarrative?.locationMonthlyCost ?? null,
              }).methodology;
              const legacy = {
                title: 'Methodology',
                description:
                  'Modeled monthly driving fuel cost per resident from VMT, fuel economy, and gasoline price inputs.',
                dataSources: [
                  'Replica (or pipeline) modeled vehicle miles traveled',
                  'Fuel-economy assumptions in transportation_burden pipeline',
                  'Gasoline price source used by transportation_burden pipeline',
                ],
                methodology: [
                  'This indicator estimates driving fuel cost only. It is not a complete household transportation-burden measure.',
                ],
              };
              const props = resolveMethodologyContentProps(authoredM, legacy);
              return <MethodologyContent {...props} />;
            })()
          }
          activeView={activeFuelCostView}
          onViewChange={(view: string) =>
            setActiveFuelCostView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
          }
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          appendLocationToTitle={true}
          overviewSectionId="transportation"
          registerPng={false}
        />
        </div>
      </SectionWrapper>

      {/* Walkability (EPA National Walkability Index) — only when tract-level walkability data exist for this location */}
      {showWalkabilitySection && isSectionEnabled('walkability') ? (
      <SectionWrapper sectionId="walkability" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
        <ChartLayout
          title="Walkability"
          description="EPA National Walkability Index at tract scale: key metrics, map, and class counts aligned with Livability → Mobility → Walkability."
          chartType="map"
          layout="vertical"
          chartContent={
            <WalkabilityTractSection location={currentLocation} geofips={selectedGeofips ?? null} />
          }
          overviewContent={
            (() => {
              const authored = buildWalkabilityTransportAuthoredBundle(currentLocation, walkabilityMetrics);
              return (
                <OverviewContent
                  title="Walkability overview"
                  description={authored.overview || walkabilityTransportOverviewDescription()}
                >
                  <p>{authored.keyInsights || `Loading walkability data for ${currentLocation}...`}</p>
                </OverviewContent>
              );
            })()
          }
          relevanceContent={
            <AuthoredRelevancePanel
              bundle={buildWalkabilityTransportAuthoredBundle(currentLocation, walkabilityMetrics)}
            />
          }
          equityContent={
            <AuthoredEquityPanel
              bundle={buildWalkabilityTransportAuthoredBundle(currentLocation, walkabilityMetrics)}
            />
          }
          methodologyContent={
            (() => {
              const authoredM = buildWalkabilityTransportAuthoredBundle(currentLocation, walkabilityMetrics).methodology;
              const bundle = transportMapMethodology('walkability');
              const props = resolveMethodologyContentProps(authoredM, {
                title: 'Methodology',
                description: `${bundle.metricLabel} for the selected map layer.`,
                dataSources: bundle.primarySources,
                methodology: bundle.calculation,
              });
              return <MethodologyContent {...props} />;
            })()
          }
          activeView={activeWalkabilityView}
          onViewChange={(view: string) =>
            setActiveWalkabilityView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
          }
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          appendLocationToTitle={true}
          registerPng={false}
        />
      </SectionWrapper>
      ) : null}

      {/* EV Charging Infrastructure Section */}
      <SectionWrapper sectionId="ev_charging" bgMap={TRANSPORT_SECTION_BG} refs={sectionRefs} visible={sectionVisible} getSectionStyle={getSectionStyle}>
      <ChartLayout
        title="EV Charging Infrastructure"
        narrativeViewId={evChargingView}
        description="Available public charging infrastructure, local access, and comparison benchmarks."
        chartType="bar"
        chartContent={
          evChargingView === 'map' ? (
            <GenericMapTiered
              location={currentLocation}
              locationGeofips={selectedGeofips}
              mapHeightPx={TRANSPORT_MAP_HEIGHT_PX}
              hideVariableSelector={true}
              lockedVariable="total_chargers"
              onDataLoaded={(metrics) => {
                if (metrics && metrics.variable === 'total_chargers') {
                  setEvChargingNarrative({
                    locationName: currentLocation,
                    countyName: metrics.jurisdictionLabel ?? currentLocation,
                    viewId: 'map',
                    totalChargers: 0,
                    l2Chargers: 0,
                    dcFastChargers: 0,
                    mapTractMedianChargers: metrics.currentValue,
                    mapTractCount: (metrics as { tractCount?: number }).tractCount ?? null,
                  });
                }
              }}
            />
          ) : (
            <div id="ev-charging-chart">
              <EVChargingInfrastructure
                location={currentLocation}
                activeTab={evChargingView}
                onTabChange={setEvChargingView}
                onNarrativeSnapshot={setEvChargingNarrative}
              />
            </div>
          )
        }
        chartSelector={{
          options: allowedEvChargingOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evChargingView,
          onChange: (value: string) => setEvChargingView(value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map')
        }}
        activeView={activeChargingView}
        onViewChange={setActiveChargingView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        layout="side-by-side"
        onDownloadPNG={() => {
          if (evChargingView === 'map') {
            return;
          }
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
            link.download = `EV_Charging_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // TODO: implement CSV download based on chart data
        }}
        pngFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
        overviewContent={
          (() => {
            const snap = evChargingNarrative
              ? { ...evChargingNarrative, viewId: evChargingView }
              : null;
            const authored = snap ? buildEvChargingAuthoredBundle(snap) : null;
            return (
              <OverviewContent
                title="EV Charging Infrastructure Overview"
                description={authored?.overview || evChargingOverviewDescription(evChargingView)}
              >
                <p>
                  {snap
                    ? authored?.keyInsights || buildEvChargingKeyInsights(snap)
                    : `Loading charging infrastructure metrics for ${currentLocation}…`}
                </p>
              </OverviewContent>
            );
          })()
        }
        relevanceContent={
          (() => {
            const snap = evChargingNarrative
              ? { ...evChargingNarrative, viewId: evChargingView }
              : null;
            const authored = snap ? buildEvChargingAuthoredBundle(snap) : null;
            if (authored) {
              return <AuthoredRelevancePanel title="Policy Relevance" bundle={authored} />;
            }
            return (
              <AuthoredRelevancePanel
                title="Policy Relevance"
                chartId="ev_charging"
                viewId={evChargingView}
                body="Charging infrastructure can inform planning for electrification and public access."
              />
            );
          })()
        }
        equityContent={
          (() => {
            const snap = evChargingNarrative
              ? { ...evChargingNarrative, viewId: evChargingView }
              : null;
            const authored = snap ? buildEvChargingAuthoredBundle(snap) : null;
            if (authored?.equityV6 || authored?.equity) {
              return <AuthoredEquityPanel title="Equity Considerations" bundle={authored} />;
            }
            return (
              <AuthoredEquityPanel
                title="Equity Considerations"
                chartId="ev_charging"
                viewId={evChargingView}
                body="Public charging access varies by housing type and geography."
              />
            );
          })()
        }
        methodologyContent={
          (() => {
            const snap = evChargingNarrative
              ? { ...evChargingNarrative, viewId: evChargingView }
              : null;
            const authored = snap ? buildEvChargingAuthoredBundle(snap) : null;
            const structured = authored?.methodology;
            const legacy = {
              title: 'Data Sources & Methodology',
              description:
                'Detailed information about data sources and calculation methods for EV charging infrastructure analysis.',
              dataSources: [
                'U.S. Department of Energy, Alternative Fuels Data Center (AFDC) Station Locator',
                'U.S. Census Bureau American Community Survey (ACS) 5-Year Estimates for 2023',
                'California Air Resources Board (CARB) EMFAC2021 model and CARB Fleet Database for 2023',
              ],
              methodology: [
                'Charging station counts are aggregated by geographic location from AFDC database',
                'Data includes public and private charging stations with status: Available (E), Planned (P), and Temporarily Unavailable (T)',
                'Per capita calculations use 2023 population estimates from ACS',
                'EV counts include both Battery Electric Vehicles (BEVs) and Plug-in Hybrid Electric Vehicles (PHEVs)',
                'County-level data is used for comparison when city-level data is not available',
                "2030 targets based on California's goal to reach 1 million charging stations statewide",
              ],
            };
            const props = resolveMethodologyContentProps(structured, legacy);
            return <MethodologyContent {...props} />;
          })()
        }
      />
      </SectionWrapper>

      {/* Transportation-Related Maps */}
      <div
        style={{
          order: getSectionOrder('transportation_maps'),
          display: transportMapsEnabled ? 'block' : 'none'
        }}
      >
      <ChartLayout
        title="Transportation-Related Maps"
        narrativeViewId={transportMapMetrics?.variable ?? 'ev_adoption'}
        description="Interactive maps for transportation indicators such as EV adoption, charging infrastructure, and vehicle access."
        chartType="map"
        layout="vertical"
        keyMetrics={
          transportMapMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} EVs/100`
                    : transportMapMetrics.variable === 'total_evs'
                    ? transportMapMetrics.currentValue < 10
                      ? `${transportMapMetrics.currentValue.toFixed(1)} EVs/tract`
                      : `${Math.round(transportMapMetrics.currentValue).toLocaleString()} EVs/tract`
                    : transportMapMetrics.variable === 'total_chargers'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} EVSE ports/tract`
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? `${transportMapMetrics.currentValue.toFixed(2)} vehicles/HH`
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? `${transportMapMetrics.currentValue.toFixed(1)}% HHs with no vehicle`
                    : transportMapMetrics.variable === 'commute_time'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} min`
                    : `${Math.round(transportMapMetrics.currentValue).toLocaleString()}`
                }
                label={transportMapMetrics.jurisdictionLabel ?? 'Selected location'}
              />
              <KeyMetric
                value={(() => {
                  const ref = transportMapMetrics.californiaAverage;
                  if (ref == null || !Number.isFinite(ref)) return '—';
                  const v = transportMapMetrics.variable;
                  if (v === 'ev_adoption') return `${ref.toFixed(1)} EVs/100`;
                  if (v === 'total_evs')
                    return ref < 10
                      ? `${ref.toFixed(1)} EVs/tract`
                      : `${Math.round(ref).toLocaleString()} EVs/tract`;
                  if (v === 'total_chargers') return `${ref.toFixed(1)} EVSE ports/tract`;
                  if (v === 'vehicles_per_household') return `${ref.toFixed(2)} vehicles/HH`;
                  if (v === 'zero_vehicle_households') return `${ref.toFixed(1)}% HHs with no vehicle`;
                  if (v === 'commute_time') return `${ref.toFixed(1)} min`;
                  return `${Math.round(ref).toLocaleString()}`;
                })()}
                label={transportMapMetrics.referenceLabel ?? 'California reference'}
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <div id="transportation-maps-chart">
            <GenericMapTiered
              location={currentLocation}
              locationGeofips={selectedGeofips}
              mapHeightPx={TRANSPORT_MAP_HEIGHT_PX}
              onDataLoaded={handleTransportMapDataLoaded}
              allowedVariables={allowedTransportMapVariables}
              enableScopeSelector={true}
            />
          </div>
        }
        onDownloadPNG={() => {
          const container = document.querySelector('#transportation-maps-chart');
          const canvas = container?.querySelector('canvas');
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Transportation_Maps_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }}
        overviewContent={
          transportMapMetrics
            ? (() => {
                const mapInput = {
                  locationName: currentLocation,
                  layerId: transportMapMetrics.variable,
                  year: transportMapMetrics.year,
                  tractCount: transportMapMetrics.tractCount ?? 0,
                  medianTractValue: transportMapMetrics.medianTractValue ?? null,
                  minTractValue: transportMapMetrics.minValue ?? null,
                  maxTractValue: transportMapMetrics.maxValue ?? null,
                  jurisdictionValue: transportMapMetrics.currentValue,
                  referenceValue: transportMapMetrics.californiaAverage,
                  referenceLabel: transportMapMetrics.referenceLabel ?? 'California reference',
                  evCountEstimated: transportMapMetrics.evCountEstimated,
                  zeroPortTractCount: transportMapMetrics.zeroPortTractCount ?? null,
                };
                const authored = buildTransportMapAuthoredBundle(mapInput);
                return (
                  <OverviewContent
                    title="Map overview"
                    description={
                      authored.overview ||
                      transportMapOverviewDescription(
                        transportMapMetrics.variable,
                        'Transportation-related tract map layer.'
                      )
                    }
                  >
                    <p>
                      {authored.keyInsights ||
                        buildTransportMapKeyInsights(mapInput) ||
                        `Loading map narrative for ${currentLocation}…`}
                    </p>
                  </OverviewContent>
                );
              })()
            : undefined
        }
        relevanceContent={
          transportMapMetrics
            ? (() => {
                const mapInput = {
                  locationName: currentLocation,
                  layerId: transportMapMetrics.variable,
                  year: transportMapMetrics.year,
                  tractCount: transportMapMetrics.tractCount ?? 0,
                  medianTractValue: transportMapMetrics.medianTractValue ?? null,
                  minTractValue: transportMapMetrics.minValue ?? null,
                  maxTractValue: transportMapMetrics.maxValue ?? null,
                  jurisdictionValue: transportMapMetrics.currentValue,
                  referenceValue: transportMapMetrics.californiaAverage,
                  referenceLabel: transportMapMetrics.referenceLabel ?? 'California reference',
                };
                const bundle = buildTransportMapAuthoredBundle(mapInput);
                return <AuthoredRelevancePanel title="Relevance" bundle={bundle} />;
              })()
            : undefined
        }
        equityContent={
          transportMapMetrics
            ? (() => {
                const mapInput = {
                  locationName: currentLocation,
                  layerId: transportMapMetrics.variable,
                  year: transportMapMetrics.year,
                  tractCount: transportMapMetrics.tractCount ?? 0,
                  medianTractValue: transportMapMetrics.medianTractValue ?? null,
                  minTractValue: transportMapMetrics.minValue ?? null,
                  maxTractValue: transportMapMetrics.maxValue ?? null,
                  jurisdictionValue: transportMapMetrics.currentValue,
                  referenceValue: transportMapMetrics.californiaAverage,
                  referenceLabel: transportMapMetrics.referenceLabel ?? 'California reference',
                };
                const bundle = buildTransportMapAuthoredBundle(mapInput);
                return <AuthoredEquityPanel title="Equity" bundle={bundle} />;
              })()
            : undefined
        }
        methodologyContent={
          (() => {
            const layer = transportMapMetrics?.variable ?? 'ev_adoption';
            const bundle = transportMapMethodology(layer);
            const mapInput = transportMapMetrics
              ? {
                  locationName: currentLocation,
                  layerId: transportMapMetrics.variable,
                  year: transportMapMetrics.year,
                  tractCount: transportMapMetrics.tractCount ?? 0,
                  medianTractValue: transportMapMetrics.medianTractValue ?? null,
                  minTractValue: transportMapMetrics.minValue ?? null,
                  maxTractValue: transportMapMetrics.maxValue ?? null,
                  jurisdictionValue: transportMapMetrics.currentValue,
                  referenceValue: transportMapMetrics.californiaAverage,
                  referenceLabel: transportMapMetrics.referenceLabel ?? 'California reference',
                }
              : null;
            const authoredM = mapInput ? buildTransportMapAuthoredBundle(mapInput).methodology : undefined;
            const legacy = {
              title: 'Methodology',
              description: `${bundle.metricLabel} for the selected map layer.`,
              dataSources: bundle.primarySources,
              methodology: bundle.calculation,
            };
            const structured = {
              primarySources: bundle.primarySources,
              calculation: bundle.calculation,
              limitations: bundle.limitations,
              technicalDetails: bundle.technicalDetails,
            };
            const props = resolveMethodologyContentProps(authoredM ?? structured, legacy);
            return (
              <MethodologyContent
                {...props}
                primarySourcesDetailed={bundle.primarySources}
                climatePlansCalculation={bundle.calculation}
                limitations={bundle.limitations}
                technicalDetails={bundle.technicalDetails}
              />
            );
          })()
        }
        activeView={activeTransportMapView}
        onViewChange={(view: string) =>
          setActiveTransportMapView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        appendLocationToTitle={true}
        registerPng={false}
      />
      </div>
      </div>

      <CustomSectionsForPage pageId="transportation" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="transportation" locationName={currentLocation} />
    </div>
  );
};
